import { type SupplierUserRole } from '@choco-admin/types';
import { useCallback } from 'react';

import { useNotification } from '../../../../../../../contexts/NotificationContext';
import {
  useSupplierRemoveUserMutation,
  useSuppliersPageUpdateSupplierUserRolesMutation,
} from '../../../../../../../generated';
import { supplierUserRoleToGqlRole } from '../../../../../../../utils/supplierRole';

export interface UseSupplierTeamActionsConfig {
  /** Called after a successful mutation to refresh the member list. */
  onRefetch?: () => Promise<unknown>;
}

export const useSupplierTeamActions = ({
  onRefetch,
}: UseSupplierTeamActionsConfig = {}) => {
  const { addNotification } = useNotification();

  const [updateSupplierUserRoles, { loading: isUpdatingRole }] =
    useSuppliersPageUpdateSupplierUserRolesMutation();
  const [supplierRemoveUser, { loading: isRemoving }] =
    useSupplierRemoveUserMutation();

  const updateMemberRole = useCallback(
    async (userId: string, role: SupplierUserRole) => {
      try {
        // rolesToAdd OVERRIDES existing roles — send exactly one.
        await updateSupplierUserRoles({
          variables: { userId, rolesToAdd: [supplierUserRoleToGqlRole(role)] },
        });
        addNotification({
          type: 'success',
          title: 'Role Updated',
          message: 'Successfully updated the team member role.',
        });
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Update Failed',
          message: 'Failed to update the team member role. Please try again.',
        });
        throw error;
      }

      // Refetch is best-effort: the mutation already succeeded, so a refetch
      // failure (e.g. transient network) must not be reported as an update
      // failure or block the caller from clearing local state.
      await onRefetch?.().catch(() => undefined);
    },
    [updateSupplierUserRoles, addNotification, onRefetch]
  );

  const removeMember = useCallback(
    async (userId: string) => {
      try {
        // userUpdate(supplierId: null) already clears the user's supplier
        // role(s) atomically as part of the same backend transaction (see
        // handleLinkSupplier in userService). No separate role-removal call
        // is needed — splitting one atomic operation into two risks
        // orphaning the user if the second call fails.
        await supplierRemoveUser({ variables: { userId, withRoles: true } });
        addNotification({
          type: 'success',
          title: 'Team Member Removed',
          message: 'Successfully removed the member from the distributor.',
        });
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Remove Failed',
          message: 'Failed to remove the team member. Please try again.',
        });
        throw error;
      }

      // Refetch is best-effort — see updateMemberRole.
      await onRefetch?.().catch(() => undefined);
    },
    [supplierRemoveUser, addNotification, onRefetch]
  );

  return { updateMemberRole, removeMember, isUpdatingRole, isRemoving };
};
